import React, { useEffect, useMemo, useState } from "react";
import { Landmark, PiggyBank, TrendingUp, AlertOctagon, Wallet, Plus, Trash2, Banknote, Percent, Printer, Receipt } from "lucide-react";
import { toast } from "sonner";
import { API, fmtErr, money, fmtDate } from "../../lib/api";
import { useLang } from "../../lib/i18n";
import { useAuth, isManagerUp } from "../../lib/auth";
import { shopLogoUrl } from "../../lib/branding";
import { printDoc } from "../../lib/printDoc";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";

const EMPTY_SRC = {
  source: "", source_type: "bank", amount: "", interest_rate: "", interest_period: "monthly",
  term_months: "12", payment_frequency: "monthly", start_date: "", due_date: "", note: "",
};
const TYPES = ["bank", "company", "personal", "partner"];
const PERIODS = ["monthly", "yearly"];
const FREQS = ["weekly", "monthly", "quarterly", "yearly", "at_maturity"];
const EMPTY_EXP = { name: "", category: "rent", amount: "", date: "", note: "" };
const EXP_CATS = ["rent", "salary", "utilities", "marketing", "other"];

function Card({ icon: Icon, label, value, tone = "text-slate-900", testid }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-mono uppercase tracking-wider text-slate-500">{label}</span>
        <Icon className="h-5 w-5 text-emerald-600" />
      </div>
      <div className={`mt-3 font-heading text-2xl font-bold ${tone}`}>{value}</div>
    </div>
  );
}

export default function Finance() {
  const { t } = useLang();
  const { user } = useAuth();
  const managerUp = isManagerUp(user);
  const [data, setData] = useState(null);
  const [form, setForm] = useState(EMPTY_SRC);
  const [open, setOpen] = useState(false);
  const [expOpen, setExpOpen] = useState(false);
  const [expForm, setExpForm] = useState(EMPTY_EXP);
  const [printOpen, setPrintOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => API.get("/shop/finance").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const interestPreview = useMemo(() => {
    const p = Number(form.amount) || 0;
    const r = Number(form.interest_rate) || 0;
    const term = Number(form.term_months) || 0;
    if (!p || !r || !term) return 0;
    const months = form.interest_period === "yearly" ? term / 12 : term;
    return Math.round(p * (r / 100) * months * 100) / 100;
  }, [form.amount, form.interest_rate, form.term_months, form.interest_period]);

  const addSource = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await API.post("/shop/finance/capital", {
        source: form.source, source_type: form.source_type, amount: Number(form.amount),
        interest_rate: form.interest_rate === "" ? undefined : Number(form.interest_rate),
        interest_period: form.interest_period,
        term_months: form.term_months === "" ? undefined : Number(form.term_months),
        payment_frequency: form.payment_frequency,
        start_date: form.start_date || undefined, due_date: form.due_date || undefined,
        note: form.note || undefined,
      });
      toast.success(t("msg.saved"));
      setForm(EMPTY_SRC);
      setOpen(false);
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  const removeSource = async (id) => {
    if (!window.confirm(t("common.delete") + "?")) return;
    try {
      await API.delete(`/shop/finance/capital/${id}`);
      toast.success(t("msg.deleted"));
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  const addExpense = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await API.post("/shop/finance/expenses", {
        name: expForm.name, category: expForm.category, amount: Number(expForm.amount),
        date: expForm.date || undefined, note: expForm.note || undefined,
      });
      toast.success(t("msg.saved"));
      setExpForm(EMPTY_EXP);
      setExpOpen(false);
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  const removeExpense = async (id) => {
    if (!window.confirm(t("common.delete") + "?")) return;
    try {
      await API.delete(`/shop/finance/expenses/${id}`);
      toast.success(t("msg.deleted"));
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  if (!data) return <p className="text-slate-500">{t("common.loading")}</p>;

  return (
    <div className="space-y-8" data-testid="finance-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-slate-900">{t("fin.title")}</h1>
          <p className="text-sm text-slate-500 mt-1">{t("fin.note")}</p>
        </div>
        <Button variant="outline" data-testid="fin-export-pdf-button" onClick={() => setPrintOpen(true)}>
          <Printer className="h-4 w-4 mr-1.5" />{t("fin.export_pdf")}
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <Card icon={PiggyBank} label={t("fin.invested_total")} value={money(data.invested_net)} tone="text-[var(--brand,#1E3A5F)]" testid="fin-invested-total" />
        <Card icon={Wallet} label={t("fin.cash_on_hand")} value={money(data.cash_on_hand)} tone="text-emerald-700" testid="fin-cash-on-hand" />
        <Card icon={Landmark} label={t("fin.capital_recovered")} value={money(data.capital_recovered)} testid="fin-capital-recovered" />
        <Card icon={Landmark} label={t("fin.capital_out")} value={money(data.capital_out)} tone="text-amber-600" testid="fin-capital-out" />
        <Card icon={TrendingUp} label={t("fin.profit")} value={money(data.profit)} tone="text-emerald-700" testid="fin-profit" />
        <Card icon={Receipt} label={t("fin.expenses_total")} value={money(data.expenses_total)} tone="text-red-600" testid="fin-expenses-total" />
        <Card icon={AlertOctagon} label={t("fin.auction_loss")} value={money(data.auction_loss)} tone="text-red-600" testid="fin-auction-loss" />
        <Card icon={Percent} label={t("fin.capital_interest")} value={money(data.capital_interest)} tone="text-amber-700" testid="fin-capital-interest" />
      </div>
      {data.cash_on_hand < 0 && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 -mt-4" data-testid="cash-negative-hint">
          {t("fin.cash_hint")}
        </p>
      )}
      {data.cashflow?.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="cashflow-chart">
          <h3 className="font-heading text-lg font-medium text-slate-900 mb-4">{t("fin.cashflow")}</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.cashflow}>
              <XAxis dataKey="month" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip formatter={(v) => money(v)} />
              <Legend />
              <Bar dataKey="inflow" name={t("fin.cash_in")} fill="#059669" radius={[4, 4, 0, 0]} />
              <Bar dataKey="outflow" name={t("fin.cash_out")} fill="#DC2626" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="capital-sources-card">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
          <div>
            <h3 className="font-heading text-lg font-medium text-slate-900 flex items-center gap-2">
              <Banknote className="h-5 w-5 text-emerald-600" />{t("fin.capital_sources")}
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">{t("fin.sources_note")}</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="font-mono font-bold text-lg text-[var(--brand,#1E3A5F)]" data-testid="sources-total">{money(data.invested_total)}</div>
            {managerUp && (
              <Button size="sm" data-testid="new-capital-source-button" onClick={() => setOpen(true)}
                className="bg-slate-900 hover:bg-slate-800 text-white">
                <Plus className="h-4 w-4 mr-1" />{t("fin.new_source")}
              </Button>
            )}
          </div>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("fin.start_date")}</TableHead>
              <TableHead>{t("fin.source")}</TableHead>
              <TableHead>{t("fin.source_type")}</TableHead>
              <TableHead className="text-right">{t("fin.principal")}</TableHead>
              <TableHead className="text-right">{t("fin.rate_term")}</TableHead>
              <TableHead className="text-right">{t("fin.total_interest")}</TableHead>
              <TableHead>{t("fin.due_date")}</TableHead>
              {managerUp && <TableHead className="text-right">{t("common.actions")}</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.capital_sources.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center text-slate-500 py-8">{t("fin.no_sources")}</TableCell></TableRow>
            )}
            {data.capital_sources.map((s) => (
              <TableRow key={s.id} data-testid={`source-row-${s.id.slice(0, 8)}`}>
                <TableCell className="font-mono text-xs">{s.start_date || fmtDate(s.date)}</TableCell>
                <TableCell className="font-medium">
                  {s.source}
                  {s.note && <div className="text-xs text-slate-400 font-normal">{s.note}</div>}
                </TableCell>
                <TableCell>
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">
                    {s.source_type ? t(`fin.type_${s.source_type}`) : "—"}
                  </span>
                </TableCell>
                <TableCell className="text-right font-mono font-semibold text-emerald-700">{money(s.amount)}</TableCell>
                <TableCell className="text-right font-mono text-xs">
                  {s.interest_rate ? `${s.interest_rate}% / ${t(`fin.period_${s.interest_period || "monthly"}`)} × ${s.term_months}m` : "—"}
                </TableCell>
                <TableCell className="text-right font-mono text-amber-700">{s.total_interest ? money(s.total_interest) : "—"}</TableCell>
                <TableCell className="font-mono text-xs">{s.due_date || "—"}</TableCell>
                {managerUp && (
                  <TableCell className="text-right">
                    <button data-testid={`source-delete-${s.id.slice(0, 8)}`} onClick={() => removeSource(s.id)}
                      className="p-1.5 hover:bg-red-50 rounded-lg"><Trash2 className="h-4 w-4 text-red-600" /></button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="expenses-card">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
          <div>
            <h3 className="font-heading text-lg font-medium text-slate-900 flex items-center gap-2">
              <Receipt className="h-5 w-5 text-red-600" />{t("fin.expenses")}
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">{t("fin.expenses_note")}</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="font-mono font-bold text-lg text-red-600" data-testid="expenses-total">{money(data.expenses_total)}</div>
            {managerUp && (
              <Button size="sm" data-testid="new-expense-button" onClick={() => setExpOpen(true)}
                className="bg-slate-900 hover:bg-slate-800 text-white">
                <Plus className="h-4 w-4 mr-1" />{t("fin.new_expense")}
              </Button>
            )}
          </div>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("common.date")}</TableHead>
              <TableHead>{t("fin.expense_name")}</TableHead>
              <TableHead>{t("fin.expense_category")}</TableHead>
              <TableHead className="text-right">{t("common.amount")}</TableHead>
              {managerUp && <TableHead className="text-right">{t("common.actions")}</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.expenses.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-8">{t("fin.no_expenses")}</TableCell></TableRow>
            )}
            {data.expenses.map((x) => (
              <TableRow key={x.id} data-testid={`expense-row-${x.id.slice(0, 8)}`}>
                <TableCell className="font-mono text-xs">{fmtDate(x.date)}</TableCell>
                <TableCell className="font-medium">
                  {x.name}
                  {x.note && <div className="text-xs text-slate-400 font-normal">{x.note}</div>}
                </TableCell>
                <TableCell>
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">
                    {t(`fin.cat_${x.category || "other"}`)}
                  </span>
                </TableCell>
                <TableCell className="text-right font-mono font-semibold text-red-600">−{money(x.amount)}</TableCell>
                {managerUp && (
                  <TableCell className="text-right">
                    <button data-testid={`expense-delete-${x.id.slice(0, 8)}`} onClick={() => removeExpense(x.id)}
                      className="p-1.5 hover:bg-red-50 rounded-lg"><Trash2 className="h-4 w-4 text-red-600" /></button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-5 border-b border-slate-200">
          <h3 className="font-heading text-lg font-medium text-slate-900">{t("fin.ledger")}</h3>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("common.date")}</TableHead>
              <TableHead>{t("loans.number")}</TableHead>
              <TableHead>{t("loans.customer")}</TableHead>
              <TableHead className="text-right">{t("fin.capital")}</TableHead>
              <TableHead className="text-right">{t("fin.profit_col")}</TableHead>
              <TableHead className="text-right">{t("fin.penalty")}</TableHead>
              <TableHead className="text-right">{t("common.amount")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.ledger.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-10">{t("common.none")}</TableCell></TableRow>
            )}
            {data.ledger.map((r) => (
              <TableRow key={r.id} data-testid={`ledger-row-${r.id.slice(0, 8)}`}>
                <TableCell>{fmtDate(r.date)}</TableCell>
                <TableCell className="font-mono font-semibold">{r.loan_number}</TableCell>
                <TableCell>{r.customer_name}
                  {r.method === "auction" && <span className="ml-1.5 text-[10px] uppercase px-1.5 py-0.5 rounded bg-purple-100 text-purple-700">{t("fin.auction_badge")}</span>}
                  {r.payment_type === "disbursement" && <span className="ml-1.5 text-[10px] uppercase px-1.5 py-0.5 rounded bg-red-100 text-red-700">{t("fin.out_badge")}</span>}
                  {r.payment_type === "expense" && <span className="ml-1.5 text-[10px] uppercase px-1.5 py-0.5 rounded bg-orange-100 text-orange-700">{t("fin.expense_badge")}{r.category ? ` · ${t(`fin.cat_${r.category}`)}` : ""}</span>}
                  {r.loss ? <span className="ml-1 text-[10px] text-red-600 font-mono">−{money(r.loss)}</span> : null}
                </TableCell>
                <TableCell className="text-right font-mono">{money(r.capital)}</TableCell>
                <TableCell className="text-right font-mono text-emerald-700">{money(r.profit)}</TableCell>
                <TableCell className="text-right font-mono text-red-600">{r.penalty ? money(r.penalty) : "—"}</TableCell>
                <TableCell className="text-right font-mono font-semibold">{money(r.amount)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-xl max-h-[90vh] overflow-y-auto" data-testid="capital-source-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{t("fin.new_source")}</DialogTitle>
          </DialogHeader>
          <form onSubmit={addSource} className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">{t("fin.source")}</Label>
              <Input data-testid="source-name-input" required minLength={2} value={form.source}
                onChange={(e) => setForm({ ...form, source: e.target.value })} className="mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">{t("fin.source_type")}</Label>
                <Select value={form.source_type} onValueChange={(v) => setForm({ ...form, source_type: v })}>
                  <SelectTrigger data-testid="source-type-select" className="mt-1 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {TYPES.map((tp) => <SelectItem key={tp} value={tp} data-testid={`source-type-${tp}`}>{t(`fin.type_${tp}`)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">{t("fin.principal")} (USD)</Label>
                <Input data-testid="source-amount-input" type="number" required min="0.01" step="0.01" value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">{t("fin.interest_rate")} %</Label>
                <Input data-testid="source-rate-input" type="number" min="0" max="100" step="0.01" value={form.interest_rate}
                  onChange={(e) => setForm({ ...form, interest_rate: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">{t("fin.interest_period")}</Label>
                <Select value={form.interest_period} onValueChange={(v) => setForm({ ...form, interest_period: v })}>
                  <SelectTrigger data-testid="source-period-select" className="mt-1 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {PERIODS.map((p) => <SelectItem key={p} value={p} data-testid={`source-period-${p}`}>{t(`fin.period_${p}`)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">{t("fin.term_months")}</Label>
                <Input data-testid="source-term-input" type="number" min="1" max="360" value={form.term_months}
                  onChange={(e) => setForm({ ...form, term_months: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">{t("fin.payment_frequency")}</Label>
                <Select value={form.payment_frequency} onValueChange={(v) => setForm({ ...form, payment_frequency: v })}>
                  <SelectTrigger data-testid="source-freq-select" className="mt-1 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {FREQS.map((f) => <SelectItem key={f} value={f} data-testid={`source-freq-${f}`}>{t(`fin.freq_${f}`)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="border border-slate-200 rounded-lg p-3 flex items-center justify-between bg-slate-50" data-testid="source-interest-preview">
              <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                {t("fin.total_interest_preview")}<br />
                <span className="normal-case font-normal text-slate-400">{t("fin.preview_formula")}</span>
              </span>
              <span className="font-mono font-bold text-xl text-slate-900">{money(interestPreview)}</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">{t("fin.start_date")}</Label>
                <Input data-testid="source-start-input" type="date" value={form.start_date}
                  onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">{t("fin.due_date")} ({t("common.optional")})</Label>
                <Input data-testid="source-due-input" type="date" value={form.due_date}
                  onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="mt-1" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">{t("fin.note")}</Label>
              <textarea data-testid="source-note-input" rows={2} value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                className="mt-1 w-full rounded-md border border-input bg-white px-3 py-2 text-sm" />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
              <Button type="submit" data-testid="source-save-button" disabled={busy}
                className="bg-slate-900 hover:bg-slate-800 text-white">{t("common.save")}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={expOpen} onOpenChange={setExpOpen}>
        <DialogContent className="bg-white max-w-md" data-testid="expense-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{t("fin.new_expense")}</DialogTitle>
          </DialogHeader>
          <form onSubmit={addExpense} className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">{t("fin.expense_name")}</Label>
              <Input data-testid="expense-name-input" required minLength={2} value={expForm.name}
                onChange={(e) => setExpForm({ ...expForm, name: e.target.value })} className="mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">{t("fin.expense_category")}</Label>
                <Select value={expForm.category} onValueChange={(v) => setExpForm({ ...expForm, category: v })}>
                  <SelectTrigger data-testid="expense-category-select" className="mt-1 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {EXP_CATS.map((c) => <SelectItem key={c} value={c} data-testid={`expense-cat-${c}`}>{t(`fin.cat_${c}`)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">{t("common.amount")} (USD)</Label>
                <Input data-testid="expense-amount-input" type="number" required min="0.01" step="0.01" value={expForm.amount}
                  onChange={(e) => setExpForm({ ...expForm, amount: e.target.value })} className="mt-1" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">{t("common.date")}</Label>
              <Input data-testid="expense-date-input" type="date" value={expForm.date}
                onChange={(e) => setExpForm({ ...expForm, date: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">{t("fin.note")}</Label>
              <textarea data-testid="expense-note-input" rows={2} value={expForm.note}
                onChange={(e) => setExpForm({ ...expForm, note: e.target.value })}
                className="mt-1 w-full rounded-md border border-input bg-white px-3 py-2 text-sm" />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setExpOpen(false)}>{t("common.cancel")}</Button>
              <Button type="submit" data-testid="expense-save-button" disabled={busy}
                className="bg-slate-900 hover:bg-slate-800 text-white">{t("common.save")}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={printOpen} onOpenChange={setPrintOpen}>
        <DialogContent className="bg-white max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="fin-report-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{t("fin.report_title")}</DialogTitle>
          </DialogHeader>
          <div className="print-area bg-white text-sm" data-testid="fin-report-body">
            <div className="flex items-center gap-4 border-b-2 border-[var(--brand,#1E3A5F)] pb-3">
              {user?.shop?.logo_path && (
                <img src={shopLogoUrl(user.shop)} alt={user.shop.name} data-testid="fin-report-logo"
                  className="h-12 w-12 shrink-0 rounded-full border-2 border-[var(--brand,#1E3A5F)] object-cover" />
              )}
              <div>
                <div className="font-heading font-bold text-xl text-[var(--brand,#1E3A5F)] uppercase tracking-tight">{user?.shop?.name}</div>
                {user?.shop?.header_text && <div className="text-xs text-slate-600">{user.shop.header_text}</div>}
                {user?.shop?.company_address && <div className="text-xs text-slate-500">{user.shop.company_address}</div>}
              </div>
            </div>
            <h2 className="text-center font-heading font-bold text-lg my-4">{t("fin.report_title")}</h2>
            <div className="text-[10px] text-slate-400 text-center -mt-3 mb-4">{t("fin.generated")}: {new Date().toLocaleString()}</div>
            <table className="w-full text-xs border border-slate-300 mb-5">
              <tbody>
                {[
                  [t("fin.invested_total"), data.invested_net, t("fin.capital_interest"), data.capital_interest],
                  [t("fin.cash_on_hand"), data.cash_on_hand, t("fin.capital_out"), data.capital_out],
                  [t("fin.total_disbursed"), data.total_disbursed, t("fin.capital_recovered"), data.capital_recovered],
                  [t("fin.capital_recovered"), data.capital_recovered, t("fin.profit"), data.profit],
                  [t("fin.penalty_collected"), data.penalty_collected, t("fin.auction_loss"), data.auction_loss],
                  [t("fin.gross_profit"), data.gross_profit, t("fin.expenses_total"), data.expenses_total],
                ].map(([l1, v1, l2, v2], i) => (
                  <tr key={i} className="border-b border-slate-200">
                    <td className="py-1.5 pl-2 font-bold text-slate-600 w-[25%]">{l1}</td>
                    <td className="py-1.5 font-mono w-[25%]">{money(v1)}</td>
                    <td className="py-1.5 pl-2 font-bold text-slate-600 w-[25%]">{l2}</td>
                    <td className="py-1.5 font-mono">{v2 == null ? "" : money(v2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="font-bold text-[var(--brand,#1E3A5F)] text-xs uppercase tracking-wider mb-1">{t("fin.capital_sources")}</div>
            <table className="w-full text-xs border border-slate-300 mb-5">
              <thead>
                <tr className="bg-slate-50">
                  <th className="text-left py-1.5 pl-2">{t("fin.start_date")}</th>
                  <th className="text-left py-1.5">{t("fin.source")}</th>
                  <th className="text-left py-1.5">{t("fin.source_type")}</th>
                  <th className="text-right py-1.5">{t("fin.principal")}</th>
                  <th className="text-right py-1.5 pr-2">{t("fin.total_interest")}</th>
                </tr>
              </thead>
              <tbody>
                {data.capital_sources.length === 0 && (
                  <tr><td colSpan={5} className="text-center text-slate-400 py-3">{t("fin.no_sources")}</td></tr>
                )}
                {data.capital_sources.map((s) => (
                  <tr key={s.id} className="border-b border-slate-100">
                    <td className="py-1.5 pl-2 font-mono">{s.start_date || fmtDate(s.date)}</td>
                    <td className="py-1.5">{s.source}</td>
                    <td className="py-1.5">{s.source_type ? t(`fin.type_${s.source_type}`) : "—"}</td>
                    <td className="py-1.5 text-right font-mono">{money(s.amount)}</td>
                    <td className="py-1.5 pr-2 text-right font-mono">{s.total_interest ? money(s.total_interest) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="font-bold text-[var(--brand,#1E3A5F)] text-xs uppercase tracking-wider mb-1">{t("fin.expenses")}</div>
            <table className="w-full text-xs border border-slate-300 mb-5">
              <thead>
                <tr className="bg-slate-50">
                  <th className="text-left py-1.5 pl-2">{t("common.date")}</th>
                  <th className="text-left py-1.5">{t("fin.expense_name")}</th>
                  <th className="text-left py-1.5">{t("fin.expense_category")}</th>
                  <th className="text-right py-1.5 pr-2">{t("common.amount")}</th>
                </tr>
              </thead>
              <tbody>
                {data.expenses.length === 0 && (
                  <tr><td colSpan={4} className="text-center text-slate-400 py-3">{t("fin.no_expenses")}</td></tr>
                )}
                {data.expenses.map((x) => (
                  <tr key={x.id} className="border-b border-slate-100">
                    <td className="py-1.5 pl-2 font-mono">{fmtDate(x.date)}</td>
                    <td className="py-1.5">{x.name}</td>
                    <td className="py-1.5">{t(`fin.cat_${x.category || "other"}`)}</td>
                    <td className="py-1.5 pr-2 text-right font-mono">−{money(x.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="font-bold text-[var(--brand,#1E3A5F)] text-xs uppercase tracking-wider mb-1">{t("fin.ledger")}</div>
            <table className="w-full text-xs border border-slate-300">
              <thead>
                <tr className="bg-slate-50">
                  <th className="text-left py-1.5 pl-2">{t("common.date")}</th>
                  <th className="text-left py-1.5">{t("loans.number")}</th>
                  <th className="text-left py-1.5">{t("loans.customer")}</th>
                  <th className="text-right py-1.5">{t("fin.capital")}</th>
                  <th className="text-right py-1.5">{t("fin.profit_col")}</th>
                  <th className="text-right py-1.5">{t("fin.penalty")}</th>
                  <th className="text-right py-1.5 pr-2">{t("common.amount")}</th>
                </tr>
              </thead>
              <tbody>
                {data.ledger.map((r) => (
                  <tr key={r.id} className="border-b border-slate-100">
                    <td className="py-1 pl-2 font-mono">{fmtDate(r.date)}</td>
                    <td className="py-1 font-mono">{r.loan_number}</td>
                    <td className="py-1">{r.customer_name}</td>
                    <td className="py-1 text-right font-mono">{money(r.capital)}</td>
                    <td className="py-1 text-right font-mono">{money(r.profit)}</td>
                    <td className="py-1 text-right font-mono">{r.penalty ? money(r.penalty) : "—"}</td>
                    <td className="py-1 pr-2 text-right font-mono font-semibold">{money(r.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="border-t border-[var(--brand,#1E3A5F)] mt-6 pt-2 text-[10px] text-slate-500 text-center">
              © {new Date().getFullYear()} {user?.shop?.name}. All Rights Reserved.
              {user?.shop?.footer_text && <div className="mt-1">{user.shop.footer_text}</div>}
            </div>
          </div>
          <Button data-testid="fin-report-print-button"
            onClick={() => printDoc(document.querySelector('[data-testid="fin-report-body"]'), { title: t("fin.report_title") })}
            className="w-full bg-slate-900 hover:bg-slate-800 text-white mt-4">
            <Printer className="h-4 w-4 mr-1.5" />{t("repay.print")}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
